import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

export default function Privacy() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  const headingClass = isDark ? 'text-white' : 'text-slate-900';
  const subheadingClass = isDark ? 'text-white' : 'text-slate-800';
  const textClass = isDark ? 'text-gray-300' : 'text-slate-600';

  return (
    <StaticPageLayout
      title="Privacy Policy"
      subtitle="Last updated: December 1, 2024"
    >
      <div className={`prose prose-lg max-w-none ${isDark ? 'prose-invert' : ''}`}>
        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>1. Introduction</h2>
          <p className={`${textClass} mb-4`}>
            SweetPilot Inc. ("SweetPilot," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI employee platform and services.
          </p>
          <p className={textClass}>
            By using SweetPilot, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>2. Information We Collect</h2>

          <h3 className={`text-xl font-semibold ${subheadingClass} mb-3`}>2.1 Information You Provide</h3>
          <ul className={`list-disc pl-6 ${textClass} mb-4 space-y-2`}>
            <li><strong>Account Information:</strong> Name, email address, password, and billing information when you create an account.</li>
            <li><strong>Chat Content:</strong> Messages and content you exchange with AI employees.</li>
            <li><strong>Brain AI Data:</strong> Documents, files, and information you upload to train your AI employees.</li>
            <li><strong>Payment Information:</strong> Credit card details and billing address (processed securely by Stripe).</li>
            <li><strong>Communications:</strong> Emails, support tickets, and feedback you send us.</li>
          </ul>

          <h3 className={`text-xl font-semibold ${subheadingClass} mb-3`}>2.2 Information Collected Automatically</h3>
          <ul className={`list-disc pl-6 ${textClass} mb-4 space-y-2`}>
            <li><strong>Usage Data:</strong> How you interact with our platform, features used, and time spent.</li>
            <li><strong>Device Information:</strong> Browser type, operating system, IP address, and device identifiers.</li>
            <li><strong>Cookies:</strong> Session cookies, analytics cookies, and preference cookies (see our Cookie Policy).</li>
            <li><strong>Log Data:</strong> Server logs including access times, pages viewed, and referring URLs.</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>3. How We Use Your Information</h2>
          <p className={`${textClass} mb-4`}>We use collected information to:</p>
          <ul className={`list-disc pl-6 ${textClass} space-y-2`}>
            <li>Provide, maintain, and improve our services</li>
            <li>Process transactions and send related information</li>
            <li>Send technical notices, updates, and support messages</li>
            <li>Respond to your comments, questions, and customer service requests</li>
            <li>Monitor and analyze usage patterns and trends</li>
            <li>Detect, prevent, and address technical issues and fraud</li>
            <li>Train and improve our AI models (with anonymized data only)</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>4. Data Sharing and Disclosure</h2>
          <p className={`${textClass} mb-4`}>We may share your information with:</p>
          <ul className={`list-disc pl-6 ${textClass} space-y-2`}>
            <li><strong>Service Providers:</strong> Third parties that help us operate our platform (hosting, analytics, payment processing).</li>
            <li><strong>AI Model Providers:</strong> We use third-party AI services. Your chat data is processed but not stored by these providers.</li>
            <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority.</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
            <li><strong>With Your Consent:</strong> When you explicitly authorize us to share information.</li>
          </ul>
          <p className={`${textClass} mt-4`}>
            We do <strong>not</strong> sell your personal information to third parties.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>5. Data Security</h2>
          <p className={`${textClass} mb-4`}>
            We implement industry-standard security measures including:
          </p>
          <ul className={`list-disc pl-6 ${textClass} space-y-2`}>
            <li>256-bit SSL/TLS encryption for all data in transit</li>
            <li>AES-256 encryption for data at rest</li>
            <li>Regular security audits and penetration testing</li>
            <li>SOC 2 Type II compliance</li>
            <li>Multi-factor authentication options</li>
            <li>Access controls and employee training</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>6. Data Retention</h2>
          <p className={`${textClass} mb-4`}>
            We retain your information for as long as your account is active or as needed to provide services. You can request deletion of your data at any time. After account deletion:
          </p>
          <ul className={`list-disc pl-6 ${textClass} space-y-2`}>
            <li>Chat history is deleted within 30 days</li>
            <li>Brain AI data is deleted within 7 days</li>
            <li>Account information is deleted within 90 days</li>
            <li>Some data may be retained for legal compliance</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>7. Your Rights</h2>
          <p className={`${textClass} mb-4`}>You have the right to:</p>
          <ul className={`list-disc pl-6 ${textClass} space-y-2`}>
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Rectification:</strong> Update or correct inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your data</li>
            <li><strong>Portability:</strong> Receive your data in a structured format</li>
            <li><strong>Restriction:</strong> Limit how we process your data</li>
            <li><strong>Objection:</strong> Object to certain processing activities</li>
            <li><strong>Withdraw Consent:</strong> Withdraw consent at any time</li>
          </ul>
          <p className={`${textClass} mt-4`}>
            To exercise these rights, contact us at privacy@flowai.com.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>8. International Transfers</h2>
          <p className={textClass}>
            Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place, including Standard Contractual Clauses approved by the European Commission.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>9. Children's Privacy</h2>
          <p className={textClass}>
            SweetPilot is not intended for children under 16. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>10. Changes to This Policy</h2>
          <p className={textClass}>
            We may update this Privacy Policy periodically. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date. Continued use of our services after changes constitutes acceptance of the updated policy.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>11. Contact Us</h2>
          <p className={textClass}>
            If you have questions about this Privacy Policy, please contact us:
          </p>
          <ul className={`list-none mt-4 ${textClass} space-y-2`}>
            <li><strong>Email:</strong> privacy@flowai.com</li>
            <li><strong>Address:</strong> SweetPilot Inc., 123 AI Street, San Francisco, CA 94102</li>
            <li><strong>Data Protection Officer:</strong> dpo@flowai.com</li>
          </ul>
        </section>
      </div>
    </StaticPageLayout>
  );
}
